import React, { useState, useEffect } from "react";
import {
  ShoppingBag,
  Upload,
  RefreshCw,
  Link,
  CheckCircle,
  AlertCircle,
  Search,
  Settings,
  Volume2,
  PhoneCall,
  DollarSign,
  Package,
} from "lucide-react";

interface CatalogViewProps {
  token: string;
  user: any;
  tenantId?: string;
}

interface CatalogItem {
  id: string;
  sku: string;
  name: string;
  category: string | null;
  price: number;
  discounted_price: number;
  stock_quantity: number;
  stock_status: string;
  image_url: string | null;
  description: string | null;
  is_active: boolean;
}

interface AISalesProfile {
  business_name: string;
  business_description: string | null;
  business_category: string | null;
  voice_persona: "aarohi" | "kabir";
  is_enabled: boolean;
  tax_inclusive: boolean;
  tax_percentage: number;
  free_shipping_threshold: number;
  standard_shipping_fee: number;
  sales_rep_phone: string | null;
  quiet_hours_start: number;
  quiet_hours_end: number;
  welcome_message: string | null;
}

export const CatalogView: React.FC<CatalogViewProps> = ({ token, user, tenantId }) => {
  const effectiveTenantId =
    user.role === "tenant"
      ? user.tenant_id
      : tenantId && tenantId !== "all"
      ? tenantId
      : user.tenant_id;

  const [activeTab, setActiveTab] = useState<"catalog" | "settings">("catalog");
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Profile settings
  const [profile, setProfile] = useState<AISalesProfile>({
    business_name: "My Business",
    business_description: "",
    business_category: "Retail",
    voice_persona: "aarohi",
    is_enabled: false,
    tax_inclusive: true,
    tax_percentage: 18.0,
    free_shipping_threshold: 999.0,
    standard_shipping_fee: 60.0,
    sales_rep_phone: "",
    quiet_hours_start: 21,
    quiet_hours_end: 9,
    welcome_message: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSavedMsg, setProfileSavedMsg] = useState("");

  // Sheet import
  const [sheetUrl, setSheetUrl] = useState("");
  const [importingSheet, setImportingSheet] = useState(false);
  const [importMsg, setImportMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const url = new URL("/api/v1/catalog/items", window.location.origin);
      if (searchQuery) url.searchParams.set("search", searchQuery);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotalCount(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch catalog items", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const url = new URL("/api/v1/catalog/ai-profile", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch (err) {
      console.error("Failed to fetch AI sales profile", err);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchProfile();
  }, [effectiveTenantId]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileSavedMsg("");
    try {
      const url = new URL("/api/v1/catalog/ai-profile", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profile),
      });
      if (res.ok) {
        setProfileSavedMsg("AI settings saved successfully!");
        setTimeout(() => setProfileSavedMsg(""), 4000);
      }
    } catch (err) {
      console.error("Failed to save profile", err);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleImportSheet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sheetUrl) return;
    setImportingSheet(true);
    setImportMsg(null);
    try {
      const url = new URL("/api/v1/catalog/import-sheet", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ sheet_url: sheetUrl }),
      });
      const data = await res.json();
      if (res.ok) {
        setImportMsg({
          type: "success",
          text: `Success! Imported ${data.imported_count} new items, updated ${data.updated_count} existing items.`,
        });
        setSheetUrl("");
        fetchItems();
      } else {
        setImportMsg({ type: "error", text: data.detail || "Import failed" });
      }
    } catch (err: any) {
      setImportMsg({ type: "error", text: err.message || "Failed to sync Google Sheet" });
    } finally {
      setImportingSheet(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setImportingSheet(true);
    setImportMsg(null);
    try {
      const url = new URL("/api/v1/catalog/import-file", window.location.origin);
      if (effectiveTenantId) url.searchParams.set("tenant_id", effectiveTenantId);
      const res = await fetch(url.toString(), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setImportMsg({
          type: "success",
          text: `File uploaded! Imported ${data.imported_count} items, updated ${data.updated_count} items.`,
        });
        fetchItems();
      } else {
        setImportMsg({ type: "error", text: data.detail || "Import failed" });
      }
    } catch (err: any) {
      setImportMsg({ type: "error", text: err.message || "File upload failed" });
    } finally {
      setImportingSheet(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900 text-white p-6 rounded-2xl shadow-xl">
        <div>
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-8 h-8 text-emerald-400" />
            <h1 className="text-2xl font-bold tracking-tight">Catalog & Autonomous AI Sales</h1>
          </div>
          <p className="text-slate-300 text-sm mt-1">
            Manage your products, prices, Google Sheets sync, and AI sales voice persona.
          </p>
        </div>
        <div className="flex bg-slate-800 p-1 rounded-xl border border-slate-700">
          <button
            onClick={() => setActiveTab("catalog")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === "catalog"
                ? "bg-emerald-600 text-white shadow-md"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Package className="w-4 h-4" /> Products ({totalCount})
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === "settings"
                ? "bg-emerald-600 text-white shadow-md"
                : "text-slate-300 hover:text-white"
            }`}
          >
            <Settings className="w-4 h-4" /> AI Sales & Voice
          </button>
        </div>
      </div>

      {importMsg && (
        <div
          className={`p-4 rounded-xl flex items-center gap-3 text-sm font-medium ${
            importMsg.type === "success"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-rose-50 text-rose-800 border border-rose-200"
          }`}
        >
          {importMsg.type === "success" ? (
            <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
          )}
          <span>{importMsg.text}</span>
        </div>
      )}

      {/* Catalog Tab */}
      {activeTab === "catalog" && (
        <div className="space-y-6">
          {/* Ingestion Box */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Google Sheets Sync */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Link className="w-5 h-5 text-emerald-600" />
                <h2 className="font-semibold text-slate-800">Sync from Google Sheets</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Paste your Google Sheet link (must have columns: SKU, Name, Price, Stock, Image).
              </p>
              <form onSubmit={handleImportSheet} className="flex gap-2">
                <input
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  value={sheetUrl}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  required
                />
                <button
                  type="submit"
                  disabled={importingSheet}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition flex items-center gap-2 shrink-0 disabled:opacity-50"
                >
                  {importingSheet ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sync Now
                </button>
              </form>
            </div>

            {/* CSV / Excel Drag and Drop */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-5 h-5 text-blue-600" />
                  <h2 className="font-semibold text-slate-800">Upload CSV / Excel File</h2>
                </div>
                <p className="text-xs text-slate-500 mb-4">
                  Upload a spreadsheet directly from your computer (.csv, .xlsx).
                </p>
              </div>
              <div>
                <label className="cursor-pointer inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl text-sm font-medium text-slate-700 bg-slate-50 hover:bg-emerald-50/50 transition">
                  <Upload className="w-4 h-4 text-emerald-600" />
                  <span>Choose File to Upload</span>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={importingSheet}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Search products by SKU or title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchItems()}
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
            <button
              onClick={fetchItems}
              className="w-full sm:w-auto px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-xl transition flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh List
            </button>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 font-medium">
                  <tr>
                    <th className="py-3.5 px-4">Item</th>
                    <th className="py-3.5 px-4">SKU</th>
                    <th className="py-3.5 px-4">Category</th>
                    <th className="py-3.5 px-4">Price</th>
                    <th className="py-3.5 px-4">Stock</th>
                    <th className="py-3.5 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-400">
                        No catalog items found. Sync a Google Sheet or upload a CSV file above.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50/80 transition">
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.name}
                                className="w-10 h-10 object-cover rounded-lg border border-slate-200"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                                <Package className="w-5 h-5" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-slate-900">{item.name}</p>
                              {item.description && (
                                <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-4 font-mono text-xs text-slate-500">{item.sku}</td>
                        <td className="py-3.5 px-4 text-xs text-slate-500">{item.category || "General"}</td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-bold text-slate-900">₹{item.discounted_price}</span>
                            {item.price > item.discounted_price && (
                              <span className="text-xs text-slate-400 line-through">₹{item.price}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 font-medium">{item.stock_quantity}</td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                              item.stock_status === "in_stock"
                                ? "bg-emerald-100 text-emerald-800"
                                : item.stock_status === "low_stock"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-rose-100 text-rose-800"
                            }`}
                          >
                            {item.stock_status === "in_stock"
                              ? "In Stock"
                              : item.stock_status === "low_stock"
                              ? "Low Stock"
                              : "Out of Stock"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <form onSubmit={handleSaveProfile} className="space-y-6">
          {profileSavedMsg && (
            <div className="p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl flex items-center gap-3 text-sm font-medium">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span>{profileSavedMsg}</span>
            </div>
          )}

          {/* Assistant Persona & Voice */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-emerald-600" />
                <h2 className="font-semibold text-slate-800">Master Voice Persona (Multilingual)</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">Enable AI Sales Assistant:</span>
                <input
                  type="checkbox"
                  checked={profile.is_enabled}
                  onChange={(e) => setProfile({ ...profile, is_enabled: e.target.checked })}
                  className="w-5 h-5 accent-emerald-600 rounded cursor-pointer"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Pre-curated studio voices that reply to customer voice notes in warm, natural Hindi, Hinglish, Marathi, and English. Zero recording required.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <label
                className={`p-4 rounded-xl border-2 cursor-pointer transition flex items-start gap-3 ${
                  profile.voice_persona === "aarohi"
                    ? "border-emerald-500 bg-emerald-50/50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="voice_persona"
                  value="aarohi"
                  checked={profile.voice_persona === "aarohi"}
                  onChange={() => setProfile({ ...profile, voice_persona: "aarohi" })}
                  className="mt-1 accent-emerald-600"
                />
                <div>
                  <p className="font-bold text-slate-900 flex items-center gap-2">
                    👩 Aarohi — Warm & Charming Female
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">Recommended</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Friendly, professional, relationship-manager cadence. Fluent in Hindi, Marathi, and English.
                  </p>
                </div>
              </label>

              <label
                className={`p-4 rounded-xl border-2 cursor-pointer transition flex items-start gap-3 ${
                  profile.voice_persona === "kabir"
                    ? "border-emerald-500 bg-emerald-50/50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="voice_persona"
                  value="kabir"
                  checked={profile.voice_persona === "kabir"}
                  onChange={() => setProfile({ ...profile, voice_persona: "kabir" })}
                  className="mt-1 accent-emerald-600"
                />
                <div>
                  <p className="font-bold text-slate-900">👨 Kabir — Confident & Authoritative Male</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Deep, reassuring, executive sales consultant tone. Perfect for high-ticket and professional services.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Business & Pricing Rules */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-800">Deterministic Commercial Rules</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Business Name</label>
                <input
                  type="text"
                  value={profile.business_name}
                  onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Business Category</label>
                <input
                  type="text"
                  value={profile.business_category || ""}
                  onChange={(e) => setProfile({ ...profile, business_category: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  placeholder="e.g. Cosmetics, Electronics, Services"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Free Shipping Threshold (₹)</label>
                <input
                  type="number"
                  value={profile.free_shipping_threshold}
                  onChange={(e) => setProfile({ ...profile, free_shipping_threshold: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Standard Shipping Fee (₹)</label>
                <input
                  type="number"
                  value={profile.standard_shipping_fee}
                  onChange={(e) => setProfile({ ...profile, standard_shipping_fee: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div className="sm:col-span-2 flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="tax_inclusive"
                  checked={profile.tax_inclusive}
                  onChange={(e) => setProfile({ ...profile, tax_inclusive: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600 rounded cursor-pointer"
                />
                <label htmlFor="tax_inclusive" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Catalog prices are inclusive of GST / Taxes
                </label>
              </div>
            </div>
          </div>

          {/* Operational & Hot Lead Alerts */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-800">Hot Lead Alerts & Quiet Hours</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Sales Manager Alert Phone</label>
                <input
                  type="text"
                  placeholder="+919876543210"
                  value={profile.sales_rep_phone || ""}
                  onChange={(e) => setProfile({ ...profile, sales_rep_phone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">Receives WhatsApp alert when lead score ≥ 75.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Quiet Hours Start (24h)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={profile.quiet_hours_start}
                  onChange={(e) => setProfile({ ...profile, quiet_hours_start: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">e.g. 21 for 9:00 PM</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Quiet Hours End (24h)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={profile.quiet_hours_end}
                  onChange={(e) => setProfile({ ...profile, quiet_hours_end: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">e.g. 9 for 9:00 AM</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingProfile}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-md transition disabled:opacity-50 flex items-center gap-2"
            >
              {savingProfile && <RefreshCw className="w-4 h-4 animate-spin" />}
              Save All AI Settings
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
